import React, { useState, useEffect } from 'react';
import axios from 'axios';

// --- Interfaces ---

interface ClientData {
  client_name: string;
  relationship: string;
  contact_number_1: string;
  contact_number_2: string;
  email: string;
}

// Interface for Doctor/Hospital/Department pairs
interface TreatingDetail {
  doctor: string;
  department: string;
  hospital: string;
}

// Helper interfaces for reading nested lists from API
interface ApiMedicalHistory {
  id: number;
  condition_name: string;
}

interface ApiDutySchedule {
  id: number;
  schedule_details: string;
  start_date: string;
}

interface PatientData {
  id: number;
  patient_name: string;
  age: string;
  sex: string;
  citizenship: string;
  blood_group: string;
  height: string;
  weight: string;
  religion: string;
  
  address: string;
  location_link: string;
  photo_filename?: string;
  photo_url?: string;
  
  // Case Info
  case_start_date: string;
  registration_date: string;
  duty_type: string;
  duty_type_specify: string;
  case_manager: string;
  status: string;
  billing_pattern: string;
  customer_category: string;
  
  // Arrays & Nested Objects
  // Note: Backend names might be 'patient_clients', etc.
  patient_clients: ClientData[];
  treating_details: TreatingDetail[]; 
  referred_by: string[];
  languages: string[];
  
  patient_medical_history: ApiMedicalHistory[];
  patient_duty_schedules: ApiDutySchedule[];
  
  // Scalar fields for simple display
  duty_rate: number;
  general_information: string;
  
  // Legacy/Fallback fields just in case
  clients_info?: ClientData[];
  medical_history_list?: string[];
  present_status_list?: string[];
}

interface ViewPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: number | null;
}

// --- CONSTANTS ---
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'; 
const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

const ViewPatientModal: React.FC<ViewPatientModalProps> = ({ isOpen, onClose, patientId }) => {
  const [patient, setPatient] = useState<PatientData | null>(null);
  const [loading, setLoading] = useState(false);

  // Derived state for display
  const [medicalHistory, setMedicalHistory] = useState<string[]>([]);
  const [presentStatus, setPresentStatus] = useState<string[]>([]);
  const [latestSchedule, setLatestSchedule] = useState<string>('');

  const getImageUrl = (path: string | undefined) => { 
    if (!path) return PLACEHOLDER_IMG;
    if (path.startsWith('http')) return path;
    // Handle S3 paths or local uploads
    return path.includes('patients/') ? `https://your-s3-bucket-url/${path}` : `${API_BASE_URL}/${path}`; 
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'Active': return '#16a34a'; 
      case 'Suspended': return '#eab308'; 
      case 'Stopped': return '#dc2626'; 
      default: return '#64748b'; 
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB'); 
  };

  useEffect(() => {
    if (isOpen && patientId) {
      const fetchPatientDetails = async () => {
        setLoading(true);
        const token = localStorage.getItem('accessToken');
        try {
          const response = await axios.get(`${API_BASE_URL}/api/patients/${patientId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = response.data;
          setPatient(data);

          // --- Process Nested Data for Display ---
          
          // 1. Split Medical History vs Present Status
          const historyData = data.patient_medical_history || [];
          const conds: string[] = [];
          const stats: string[] = [];
          
          if (Array.isArray(historyData)) {
             historyData.forEach((h: any) => {
                 const val = typeof h === 'string' ? h : (h.condition_name || '');
                 if (val.startsWith("Present Status: ")) {
                     stats.push(val.replace("Present Status: ", ""));
                 } else {
                     conds.push(val);
                 }
             });
          }
          setMedicalHistory(conds);
          setPresentStatus(stats);

          // 2. Get Latest Schedule
          const schedules = data.patient_duty_schedules || [];
          if (schedules.length > 0) {
              setLatestSchedule(schedules[schedules.length - 1].schedule_details || '-');
          } else {
              setLatestSchedule(data.duty_schedule || '-');
          }

        } catch (error) {
          console.error('Failed to fetch details:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchPatientDetails();
    }
  }, [isOpen, patientId]);

  const handlePrint = () => {
    window.print();
  };

  if (!isOpen) return null;

  // --- Styles ---
  const styles: { [key: string]: React.CSSProperties } = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(2px)' },
    modal: { backgroundColor: 'white', borderRadius: '16px', width: '95%', maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' },
    header: { padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
    title: { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
    headerBtns: { display: 'flex', gap: '1rem', alignItems: 'center' },
    printBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#f1f5f9', color: '#334155', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
    closeBtn: { background: 'none', border: 'none', fontSize: '2rem', lineHeight: '1rem', cursor: 'pointer', color: '#94a3b8', transition: 'color 0.2s' },
    body: { padding: '2rem' },
    section: { marginBottom: '2.5rem' },
    sectionTitle: { fontSize: '1.1rem', fontWeight: 700, color: '#334155', marginBottom: '1.25rem', borderLeft: '4px solid #3b82f6', paddingLeft: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
    grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' },
    field: { display: 'flex', flexDirection: 'column' },
    label: { fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase', fontWeight: 600 },
    value: { fontSize: '1rem', color: '#1e293b', fontWeight: 500, lineHeight: '1.5', whiteSpace: 'pre-wrap' },
    profileHeader: { display: 'flex', gap: '2rem', alignItems: 'center', marginBottom: '2rem', backgroundColor: '#f8fafc', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0' },
    avatar: { width: '100px', height: '100px', borderRadius: '50px', objectFit: 'cover', border: '3px solid white', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' },
    badge: { display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '9999px', color: 'white', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
    tagContainer: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
    tag: { backgroundColor: '#e2e8f0', color: '#475569', padding: '0.25rem 0.75rem', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 500 },
    link: { color: '#2563eb', textDecoration: 'none', fontWeight: 500 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
    th: { textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' },
    td: { padding: '0.75rem', borderBottom: '1px solid #f1f5f9', color: '#334155' },
    list: { margin: 0, paddingLeft: '1.2rem', color: '#334155' },
    listItem: { marginBottom: '0.25rem' }
  };

  const Field = ({ label, value }: { label: string, value: React.ReactNode }) => (
    <div style={styles.field}>
      <span style={styles.label}>{label}</span>
      <div style={styles.value}>{value || '-'}</div>
    </div>
  );

  return (
    <>
    <style>{`
      @media print {
        body * { visibility: hidden; }
        #printable-modal, #printable-modal * { visibility: visible; }
        #printable-modal { position: absolute; left: 0; top: 0; width: 100%; height: auto; overflow: visible !important; background: white; padding: 0; margin: 0; box-shadow: none !important; border-radius: 0 !important; }
        .no-print { display: none !important; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .profile-header { border: 1px solid #ddd !important; }
      }
    `}</style>

    <div style={styles.overlay} onClick={onClose}>
      <div id="printable-modal" style={styles.modal} onClick={e => e.stopPropagation()}>
        
        <div style={styles.header}>
          <h2 style={styles.title}>Patient Details</h2>
          <div style={styles.headerBtns} className="no-print">
            <button style={styles.printBtn} onClick={handlePrint} title="Save as PDF">
              🖨️ Print / PDF
            </button>
            <button style={styles.closeBtn} onClick={onClose}>&times;</button>
          </div>
        </div>

        <div style={styles.body}>
          {loading ? (
            <div style={{textAlign: 'center', padding: '4rem'}}>Loading profile...</div>
          ) : patient ? (
            <>
              {/* --- 1. PROFILE HEADER --- */}
              <div className="profile-header" style={styles.profileHeader}>
                <img 
                  src={getImageUrl(patient.photo_url || patient.photo_filename)} 
                  alt="Profile" 
                  style={styles.avatar} 
                  onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
                />
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem' }}>{patient.patient_name}</h3>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <span style={{...styles.badge, backgroundColor: getStatusColor(patient.status)}}>{patient.status}</span>
                    <span style={{color: '#64748b', fontSize: '0.95rem', fontWeight: 500}}>Case ID: #{patient.id}</span>
                  </div>
                </div>
                
                <div style={{ textAlign: 'right', borderLeft: '1px solid #e2e8f0', paddingLeft: '2rem' }}>
                   <div style={{fontWeight: 500, marginBottom: '0.25rem', color: '#334155'}}>Manager: {patient.case_manager || 'Not Assigned'}</div>
                   <div style={{color: '#64748b', fontSize: '0.9rem'}}>Registered: {formatDate(patient.registration_date)}</div>
                </div>
              </div>

              {/* --- 2. PERSONAL INFORMATION --- */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>Personal Information</h4>
                <div style={styles.grid3}>
                  <Field label="Age" value={patient.age ? `${patient.age}` : '-'} />
                  <Field label="Gender" value={patient.sex} />
                  <Field label="Blood Group" value={patient.blood_group} />
                  <Field label="Height" value={patient.height ? `${patient.height} cm` : '-'} />
                  <Field label="Weight" value={patient.weight ? `${patient.weight} kg` : '-'} />
                  <Field label="Citizenship" value={patient.citizenship} />
                  <Field label="Religion" value={patient.religion} />
                </div>
                <div style={{marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem'}}>
                    <Field label="Residential Address" value={patient.address} />
                    <Field label="Location Map" value={
                        patient.location_link ? (
                            <a href={patient.location_link} target="_blank" rel="noopener noreferrer" style={styles.link}>
                                View on Google Maps ↗
                            </a>
                        ) : '-'
                    } />
                </div>
                <div style={{marginTop: '1.5rem'}}>
                    <Field label="Languages Spoken" value={
                        <div style={styles.tagContainer}>
                          {patient.languages && patient.languages.length > 0 
                            ? patient.languages.map((l, i) => <span key={i} style={styles.tag}>{l}</span>)
                            : '-'
                          }
                        </div>
                    } />
                </div>
              </div>

              {/* --- 3. MEDICAL INFORMATION --- */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>Medical Information</h4>
                
                <div style={styles.grid2}>
                    {/* List 1: Medical History */}
                    <Field label="Medical Condition / History" value={
                        medicalHistory.length > 0 ? (
                            <ul style={styles.list}>
                                {medicalHistory.map((item, i) => <li key={i} style={styles.listItem}>{item}</li>)}
                            </ul>
                        ) : '-'
                    } />

                    {/* List 2: Present Status */}
                    <Field label="Present Status" value={
                        presentStatus.length > 0 ? (
                            <ul style={styles.list}>
                                {presentStatus.map((item, i) => <li key={i} style={styles.listItem}>{item}</li>)}
                            </ul>
                        ) : '-'
                    } />
                </div>

                <div style={{marginTop: '1.5rem'}}>
                    <h5 style={{fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem', fontWeight: 600, textTransform: 'uppercase'}}>TREATING DETAILS</h5>
                    {patient.treating_details && patient.treating_details.length > 0 ? (
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>Doctor Name</th>
                            <th style={styles.th}>Department</th>
                            <th style={styles.th}>Hospital Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {patient.treating_details.map((detail, idx) => (
                            <tr key={idx}>
                              <td style={{...styles.td, fontWeight: 500}}>{detail.doctor || '-'}</td>
                              <td style={styles.td}>{detail.department || '-'}</td>
                              <td style={styles.td}>{detail.hospital || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p style={{color: '#94a3b8', fontSize: '0.9rem'}}>No treating details recorded.</p>
                    )}
                </div>
              </div>

              {/* --- 4. CASE CONFIGURATION --- */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>Case Configuration</h4>
                <div style={styles.grid3}>
                    <Field label="Case Start Date" value={formatDate(patient.case_start_date)} />
                    
                    <Field label="Duty Type" value={
                        patient.duty_type === 'Specify' 
                        ? `Custom: ${patient.duty_type_specify}` 
                        : patient.duty_type
                    } />
                    
                    <Field label="Duty Schedule" value={latestSchedule} />
                    
                    <Field label="Duty Rate" value={patient.duty_rate ? `₹ ${patient.duty_rate}` : '-'} />
                    
                    <Field label="Billing Pattern" value={patient.billing_pattern} />
                    
                    <Field label="Customer Category" value={patient.customer_category} />
                </div>
                
                <div style={{marginTop: '1.5rem'}}>
                    <Field label="Referred By" value={
                       <div style={styles.tagContainer}>
                         {patient.referred_by && patient.referred_by.length > 0 
                           ? patient.referred_by.map((r, i) => <span key={i} style={styles.tag}>{r}</span>)
                           : '-'
                         }
                       </div>
                    } />
                </div>
              </div>

              {/* --- 5. CLIENT / GUARDIAN DETAILS --- */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>Client / Guardian Details</h4>
                {/* Note: Check both possible keys 'patient_clients' (new) or 'clients_info' (fallback) */}
                {(patient.patient_clients && patient.patient_clients.length > 0) || (patient.clients_info && patient.clients_info.length > 0) ? (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Name</th>
                        <th style={styles.th}>Relationship</th>
                        <th style={styles.th}>Primary Contact</th>
                        <th style={styles.th}>Secondary Contact</th>
                        <th style={styles.th}>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(patient.patient_clients || patient.clients_info).map((client, idx) => (
                        <tr key={idx}>
                          <td style={{...styles.td, fontWeight: 500}}>{client.client_name || (client as any).name}</td>
                          <td style={styles.td}>{client.relationship}</td>
                          <td style={styles.td}>{client.contact_number_1 || (client as any).contact_number}</td>
                          <td style={styles.td}>{client.contact_number_2 || (client as any).secondary_contact_number || '-'}</td>
                          <td style={styles.td}>{client.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{color: '#94a3b8', fontSize: '0.9rem'}}>No client details recorded.</p>
                )}
              </div>

              {/* --- 6. GENERAL INFO --- */}
              {patient.general_information && (
                  <div style={styles.section}>
                    <h4 style={styles.sectionTitle}>General Notes</h4>
                    <div style={{backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', color: '#475569', lineHeight: '1.6'}}>
                        {patient.general_information}
                    </div>
                  </div>
              )}

            </>
          ) : (
            <p>Could not load patient data.</p>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ViewPatientModal;